/* eslint-disable no-restricted-imports */
import { useMemo, useRef, useState } from 'react';

import {
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Collapse,
  Fade,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  Paper,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import {
  Add as AddIcon,
  Assignment as AssignmentIcon,
  Build as BuildIcon,
  Check as CheckIcon,
  Code as CodeIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Message as MessageIcon,
  Notifications as NotificationsIcon,
  Search as SearchIcon,
  Send as SendIcon,
  ShoppingCart as ShoppingCartIcon,
  StarBorder as StarBorderIcon,
  Star as StarIcon,
  Timeline as TimelineIcon,
  Visibility as VisibilityIcon,
  WhatsApp as WhatsAppIcon,
} from '@material-ui/icons';

import { Alert, AlertTitle, Autocomplete } from '@mui/material';
import { Form, Formik } from 'formik';
import { useQuery } from 'hooks/useQuery';
import { PageTitle } from 'pages/Layout/PageTitle';
import { gupShupService, xpcellService } from 'services';

import { DeleteConfirmationDialog } from './components/DeleteConfirmationDialog';
import { PreviewDialog } from './components/PreviewDialog';
import { validationSchema } from './formik/validationSchema';
import { extractTemplateVariables } from './utils/extractTemplateVariables';
import { extractVariables } from './utils/extractVariables';

// Componente de estatísticas
function StatsCard({ icon, title, value, color = 'primary', subtitle }) {
  return (
    <Card
      elevation={2}
      style={{ height: '100%', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
    >
      <CardContent style={{ color: 'white' }}>
        <Box display='flex' alignItems='center' justifyContent='space-between'>
          <Box>
            <Typography variant='h4' style={{ fontWeight: 'bold', marginBottom: 8 }}>
              {value}
            </Typography>
            <Typography variant='subtitle1' style={{ opacity: 0.9 }}>
              {title}
            </Typography>
            {subtitle && (
              <Typography variant='caption' style={{ opacity: 0.7 }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          <Avatar style={{ backgroundColor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
            {icon}
          </Avatar>
        </Box>
      </CardContent>
    </Card>
  );
}

// Componente de template moderno
function ModernTemplateCard({
  template,
  onEdit,
  onDelete,
  onPreview,
  onToggleFavorite,
  isFavorite,
}) {
  const [expanded, setExpanded] = useState(false);
  const variables = extractVariables(template.mensagem);

  const getStatusColor = () => {
    if (variables.length === 0) return '#f5f5f5';
    if (variables.length <= 3) return '#e3f2fd';
    return '#fff3e0';
  };

  return (
    <Card
      elevation={2}
      style={{
        marginBottom: 16,
        borderRadius: 12,
        border: isFavorite ? '2px solid #1976d2' : '1px solid #e0e0e0',
        backgroundColor: getStatusColor(),
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 8px 25px rgba(0,0,0,0.12)',
        },
      }}
    >
      <CardHeader
        avatar={
          <Avatar style={{ backgroundColor: variables.length > 0 ? '#1976d2' : '#757575' }}>
            <MessageIcon />
          </Avatar>
        }
        title={
          <Box display='flex' alignItems='center' gap={1}>
            <Typography variant='subtitle1' style={{ fontWeight: 'bold' }}>
              {template.etapa}
            </Typography>
            <Chip size='small' label={template.processo} color='primary' variant='outlined' />
            <Chip size='small' label={template.atendimento} color='secondary' variant='outlined' />
            {isFavorite && <StarIcon style={{ color: '#ffc107', fontSize: 20 }} />}
          </Box>
        }
        subheader={
          <Box display='flex' alignItems='center' gap={1} style={{ marginTop: 8 }}>
            {variables.length > 0 && (
              <Badge badgeContent={variables.length} color='primary'>
                <Chip icon={<CodeIcon />} label='Variáveis' size='small' variant='outlined' />
              </Badge>
            )}
            <Typography variant='caption' color='textSecondary'>
              ID: {template.idCadastro}
            </Typography>
          </Box>
        }
        action={
          <Box>
            <Tooltip title={isFavorite ? 'Remover dos favoritos' : 'Adicionar aos favoritos'}>
              <IconButton onClick={() => onToggleFavorite(template.idCadastro)}>
                {isFavorite ? <StarIcon style={{ color: '#ffc107' }} /> : <StarBorderIcon />}
              </IconButton>
            </Tooltip>
            <Tooltip title='Expandir detalhes'>
              <IconButton onClick={() => setExpanded(!expanded)}>
                {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Tooltip>
          </Box>
        }
      />

      <Collapse in={expanded}>
        <CardContent style={{ paddingTop: 0 }}>
          <Typography
            variant='body2'
            style={{
              marginBottom: 16,
              padding: 12,
              backgroundColor: 'rgba(0,0,0,0.04)',
              borderRadius: 8,
              fontFamily: 'monospace',
              fontSize: 13,
              lineHeight: 1.5,
            }}
          >
            {template.mensagem}
          </Typography>

          {variables.length > 0 && (
            <Box marginBottom={2}>
              <Typography variant='subtitle2' gutterBottom>
                Variáveis utilizadas:
              </Typography>
              <Box display='flex' flexWrap='wrap' gap={1}>
                {variables.map((variable, index) => (
                  <Chip
                    key={index}
                    label={variable}
                    size='small'
                    color='primary'
                    variant='outlined'
                    icon={<CodeIcon />}
                  />
                ))}
              </Box>
            </Box>
          )}

          <Box display='flex' justifyContent='flex-end' gap={1}>
            <Button
              startIcon={<VisibilityIcon />}
              onClick={() => onPreview(template.mensagem)}
              variant='outlined'
              size='small'
            >
              Preview
            </Button>
            <Button
              startIcon={<EditIcon />}
              onClick={() => onEdit(template)}
              variant='outlined'
              color='primary'
              size='small'
            >
              Editar
            </Button>
            <Button
              startIcon={<DeleteIcon />}
              onClick={() => onDelete(template.idCadastro)}
              variant='outlined'
              color='secondary'
              size='small'
            >
              Excluir
            </Button>
          </Box>
        </CardContent>
      </Collapse>
    </Card>
  );
}

// Componente de inserção de variáveis modernizado
function ModernVariableInserter({ availableVariables, onInsertVariable, disabled }) {
  const [searchTerm, setSearchTerm] = useState('');

  const filteredVariables = availableVariables.filter(
    (variable) =>
      variable.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      variable.description.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  return (
    <Card variant='outlined' style={{ borderRadius: 8 }}>
      <CardContent>
        <Typography
          variant='subtitle2'
          gutterBottom
          style={{ display: 'flex', alignItems: 'center' }}
        >
          <CodeIcon style={{ marginRight: 8 }} />
          Variáveis Disponíveis
        </Typography>

        <TextField
          fullWidth
          size='small'
          placeholder='Buscar variáveis...'
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          style={{ marginBottom: 16 }}
        />

        <Box display='flex' flexWrap='wrap' gap={1}>
          {filteredVariables.map((variable, index) => (
            <Tooltip key={index} title={variable.description}>
              <Chip
                label={variable.name}
                onClick={() => !disabled && onInsertVariable(variable.name)}
                color='primary'
                variant='outlined'
                size='small'
                clickable={!disabled}
                style={{
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  opacity: disabled ? 0.5 : 1,
                }}
              />
            </Tooltip>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
}

// Componente principal
export function MessageConfiguration() {
  const formikRef = useRef();

  const [currentTab, setCurrentTab] = useState(0);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [previewContent, setPreviewContent] = useState('');
  const [openPreview, setOpenPreview] = useState(false);
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [variableMappings, setVariableMappings] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [favorites, setFavorites] = useState(new Set());
  const [showOnlyFavorites, setShowOnlyFavorites] = useState(false);

  const [messages, , loadingMessages, refetchMessages] = useQuery(
    () => xpcellService.getSettingsMessage(),
    [],
  );

  const [templates, , loadingTemplates] = useQuery(() => gupShupService.getTemplates(), []);
  const [process, , loadingProcess] = useQuery(() => xpcellService.getProcess(), []);
  const [serviceTypes, , loadingServiceTypes] = useQuery(() => xpcellService.getServiceTypes(), []);
  const [steps, , loadingSteps] = useQuery(() => xpcellService.getSteps(), []);

  const processOptions = (process || []).map((option) => ({
    label: option.descricao,
    value: option.idCadastro,
  }));

  const serviceTypesOptions = (serviceTypes || []).map((option) => ({
    label: option.descricao,
    value: option.idCadastro,
  }));

  const stepsOptions = (steps || []).map((option) => ({
    label: option.descricao,
    value: option.idCadastro,
  }));

  const templateOptions = (templates?.templates || []).map((template) => ({
    label: template.elementName,
    value: template.id,
    template: template,
  }));

  // Variáveis específicas para cada categoria
  const SERVICE_ORDER_VARIABLES = [
    { name: '{cliente_nome}', description: 'Nome do cliente' },
    { name: '{objeto_correios}', description: 'Objeto gerado na expedição' },
  ];

  const SALES_VARIABLES = [
    { name: '{cliente_nome}', description: 'Nome do cliente' },
    { name: '{produto_nome}', description: 'Nome do produto' },
    { name: '{valor_original}', description: 'Valor do produto' },
    { name: '{valor_venda}', description: 'Valor do produto' },
    { name: '{vendedor_nome}', description: 'Nome do vendedor' },
    { name: '{data_venda}', description: 'Data da venda' },
  ];

  // Filtrar e organizar mensagens
  const { serviceOrderMessages, salesMessages, filteredServiceMessages, filteredSalesMessages } =
    useMemo(() => {
      const allMessages = messages || [];
      const serviceOrders = allMessages.filter(
        (msg) => msg.categoria === 'ordem_servico' || !msg.categoria,
      );
      const sales = allMessages.filter((msg) => msg.categoria === 'vendas');

      const filterMessages = (msgs) => {
        return msgs.filter((template) => {
          const matchesSearch =
            template.etapa?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            template.processo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            template.atendimento?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            template.mensagem?.toLowerCase().includes(searchTerm.toLowerCase());

          const matchesFavorites = !showOnlyFavorites || favorites.has(template.idCadastro);

          return matchesSearch && matchesFavorites;
        });
      };

      return {
        serviceOrderMessages: serviceOrders,
        salesMessages: sales,
        filteredServiceMessages: filterMessages(serviceOrders),
        filteredSalesMessages: filterMessages(sales),
      };
    }, [messages, searchTerm, favorites, showOnlyFavorites]);

  const getCurrentCategoryVariables = () => {
    return currentTab === 0 ? SERVICE_ORDER_VARIABLES : SALES_VARIABLES;
  };

  const getCurrentCategoryType = () => {
    return currentTab === 0 ? 'ordem_servico' : 'vendas';
  };

  const createTemplate = async (data) => {
    try {
      const templateData = {
        ...data,
        categoria: getCurrentCategoryType(),
      };
      await xpcellService.createSettingsMessage(templateData);
      refetchMessages();
      setSnackbar({ open: true, message: '✅ Template criado com sucesso!', severity: 'success' });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.message || '❌ Erro ao criar o template',
        severity: 'error',
      });
    }
  };

  const updateTemplate = async (data) => {
    try {
      const templateData = {
        ...data,
        categoria: getCurrentCategoryType(),
      };
      await xpcellService.updateSettingsMessage(templateData);
      refetchMessages();
      setSnackbar({
        open: true,
        message: '✅ Template atualizado com sucesso!',
        severity: 'success',
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.message || '❌ Erro ao atualizar o template',
        severity: 'error',
      });
    }
  };

  const handleSubmit = async (values, { resetForm }) => {
    const newTemplate = {
      process: values.process.value,
      serviceType: values.serviceType.value,
      stage: values.stage.value,
      id: editingTemplate ? editingTemplate.id : Date.now().toString(),
      message: values.message,
      templateId: selectedTemplate?.value,
      variableMappings: variableMappings,
    };

    if (editingTemplate) {
      await updateTemplate(newTemplate);
    } else {
      await createTemplate(newTemplate);
    }

    setEditingTemplate(null);
    setSelectedTemplate(null);
    setVariableMappings({});
    resetForm();
  };

  const handleEdit = (template) => {
    setEditingTemplate({
      id: template.idCadastro,
      stage: { value: template.idEtapa, label: template.etapa },
      process: { value: template.idTipoProcesso, label: template.processo },
      serviceType: { value: template.idTipoAtendimento, label: template.atendimento },
      message: template.mensagem,
    });

    if (template.templateId) {
      const matchedTemplate = templateOptions.find((opt) => opt.value === template.templateId);
      if (matchedTemplate) {
        setSelectedTemplate(matchedTemplate);
        setVariableMappings(template.variableMappings || {});
      }
    }
  };

  const handleDeleteConfirm = (id) => {
    setTemplateToDelete(id);
    setOpenDeleteConfirm(true);
  };

  const handlePreview = (message) => {
    setPreviewContent(message);
    setOpenPreview(true);
  };

  const handleTemplateSelect = (_, newValue) => {
    setSelectedTemplate(newValue);
    formikRef?.current?.setFieldValue('message', newValue?.template?.data);
    if (newValue) {
      setVariableMappings({});
    }
  };

  const handleVariableMapping = (templateVar, systemVar) => {
    setVariableMappings((prev) => ({
      ...prev,
      [templateVar]: systemVar,
    }));
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
    setEditingTemplate(null);
    setSelectedTemplate(null);
    setVariableMappings({});
  };

  const handleInsertVariable = (variableName) => {
    const currentMessage = formikRef?.current?.values?.message || '';
    const newMessage = currentMessage + (currentMessage ? ' ' : '') + variableName;
    formikRef?.current?.setFieldValue('message', newMessage);
  };

  const handleToggleFavorite = (templateId) => {
    setFavorites((prev) => {
      const newFavorites = new Set(prev);
      if (newFavorites.has(templateId)) {
        newFavorites.delete(templateId);
      } else {
        newFavorites.add(templateId);
      }
      return newFavorites;
    });
  };

  const getCurrentMessages = () => {
    return currentTab === 0 ? filteredServiceMessages : filteredSalesMessages;
  };

  const getTotalMessages = () => {
    return currentTab === 0 ? serviceOrderMessages.length : salesMessages.length;
  };

  const getMessagesWithVariables = () => {
    const messages = currentTab === 0 ? serviceOrderMessages : salesMessages;
    return messages.filter((msg) => extractVariables(msg.mensagem).length > 0).length;
  };

  const renderMessageForm = () => (
    <Fade in timeout={500}>
      <Card
        elevation={3}
        style={{
          borderRadius: 16,
          background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
        }}
      >
        <CardHeader
          avatar={
            <Avatar style={{ backgroundColor: editingTemplate ? '#ff9800' : '#4caf50' }}>
              {editingTemplate ? <EditIcon /> : <AddIcon />}
            </Avatar>
          }
          title={
            <Typography variant='h6' style={{ fontWeight: 'bold' }}>
              {editingTemplate ? 'Editar Template' : 'Criar Novo Template'}
            </Typography>
          }
          subheader={
            <Typography variant='body2' color='textSecondary'>
              {getCurrentCategoryType() === 'ordem_servico'
                ? 'Mensagem para Ordens de Serviço'
                : 'Mensagem para Vendas'}
            </Typography>
          }
        />
        <CardContent>
          <Formik
            initialValues={{
              stage: editingTemplate?.stage || '',
              process: editingTemplate?.process || '',
              message: editingTemplate?.message || '',
              serviceType: editingTemplate?.serviceType || '',
            }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            enableReinitialize
            innerRef={formikRef}
          >
            {({ values, errors, touched, setFieldValue, isValid, dirty, isSubmitting }) => (
              <Form>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Autocomplete
                      loading={loadingSteps}
                      options={stepsOptions}
                      value={values.stage}
                      onChange={(_, newValue) => setFieldValue('stage', newValue || '')}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label='Etapa da Ordem de Serviço'
                          variant='outlined'
                          fullWidth
                          error={touched.stage && Boolean(errors.stage)}
                          helperText={touched.stage && errors.stage}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Autocomplete
                      loading={loadingProcess}
                      options={processOptions}
                      value={values.process}
                      onChange={(_, newValue) => setFieldValue('process', newValue || '')}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label='Processo da Ordem de Serviço'
                          variant='outlined'
                          fullWidth
                          error={touched.process && Boolean(errors.process)}
                          helperText={touched.process && errors.process}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Autocomplete
                      loading={loadingServiceTypes}
                      options={serviceTypesOptions}
                      value={values.serviceType}
                      onChange={(_, newValue) => setFieldValue('serviceType', newValue || '')}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label='Atendimento da Ordem de Serviço'
                          variant='outlined'
                          fullWidth
                          error={touched.serviceType && Boolean(errors.serviceType)}
                          helperText={touched.serviceType && errors.serviceType}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Autocomplete
                      loading={loadingTemplates}
                      options={templateOptions}
                      value={selectedTemplate}
                      onChange={handleTemplateSelect}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label='Selecione um Template do Gupshup'
                          variant='outlined'
                          fullWidth
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <InputAdornment position='start'>
                                <WhatsAppIcon style={{ color: '#25D366' }} />
                              </InputAdornment>
                            ),
                          }}
                        />
                      )}
                    />
                  </Grid>

                  {selectedTemplate && (
                    <Grid item xs={12}>
                      <Alert severity='info' style={{ marginBottom: 16 }}>
                        <AlertTitle>Mapeamento de Variáveis</AlertTitle>
                        Configure como as variáveis do template serão preenchidas com dados do
                        sistema.
                      </Alert>
                      {extractTemplateVariables(selectedTemplate.template.data).map(
                        (templateVar) => (
                          <Box
                            key={templateVar}
                            display='flex'
                            alignItems='center'
                            marginBottom={1}
                          >
                            <Typography variant='body2' style={{ marginRight: 16, minWidth: 100 }}>
                              {templateVar} →
                            </Typography>
                            <Autocomplete
                              options={getCurrentCategoryVariables()}
                              getOptionLabel={(option) => `${option.name} (${option.description})`}
                              value={
                                getCurrentCategoryVariables().find(
                                  (v) => v.name === variableMappings[templateVar],
                                ) || null
                              }
                              onChange={(_, newValue) =>
                                handleVariableMapping(templateVar, newValue?.name || '')
                              }
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  variant='outlined'
                                  size='small'
                                  placeholder='Selecione uma variável'
                                />
                              )}
                              style={{ flexGrow: 1 }}
                            />
                          </Box>
                        ),
                      )}
                    </Grid>
                  )}

                  <Grid item xs={12}>
                    <TextField
                      name='message'
                      label='Mensagem'
                      multiline
                      rows={6}
                      fullWidth
                      variant='outlined'
                      disabled={!!selectedTemplate}
                      error={touched.message && Boolean(errors.message)}
                      helperText={
                        (touched.message && errors.message) ||
                        (selectedTemplate
                          ? 'Mensagem carregada do template Gupshup'
                          : 'Digite sua mensagem personalizada')
                      }
                      value={selectedTemplate?.template?.data || values.message}
                      onChange={(e) => setFieldValue('message', e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position='start'>
                            <MessageIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  {!selectedTemplate && (
                    <Grid item xs={12}>
                      <ModernVariableInserter
                        availableVariables={getCurrentCategoryVariables()}
                        onInsertVariable={handleInsertVariable}
                        disabled={!!selectedTemplate}
                      />
                    </Grid>
                  )}

                  <Grid item xs={12}>
                    <Box display='flex' justifyContent='space-between' alignItems='center'>
                      <Box>
                        {values.message && (
                          <Typography variant='caption' color='textSecondary'>
                            {values.message.length} caracteres •{' '}
                            {
                              extractVariables(
                                values.message || selectedTemplate?.template?.data || '',
                              ).length
                            }{' '}
                            variáveis
                          </Typography>
                        )}
                      </Box>

                      <Box display='flex' gap={2}>
                        {editingTemplate && (
                          <Button
                            variant='outlined'
                            color='secondary'
                            onClick={() => {
                              setEditingTemplate(null);
                              setSelectedTemplate(null);
                              setVariableMappings({});
                            }}
                          >
                            Cancelar
                          </Button>
                        )}
                        <Button
                          type='submit'
                          variant='contained'
                          color='primary'
                          startIcon={editingTemplate ? <CheckIcon /> : <SendIcon />}
                          disabled={!isValid || !dirty || isSubmitting}
                          size='large'
                          style={{ borderRadius: 25 }}
                        >
                          {editingTemplate ? 'Atualizar Template' : 'Salvar Template'}
                        </Button>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Form>
            )}
          </Formik>
        </CardContent>
      </Card>
    </Fade>
  );

  return (
    <Box style={{ padding: 24, backgroundColor: '#f5f7fa', minHeight: '100vh' }}>
      <PageTitle>
        <Box display='flex' alignItems='center'>
          <NotificationsIcon style={{ marginRight: 16, fontSize: 32 }} />
          Configuração de Mensagens
        </Box>
      </PageTitle>

      {/* Estatísticas */}
      <Grid container spacing={3} style={{ marginBottom: 32 }}>
        <Grid item xs={12} md={3}>
          <StatsCard
            icon={<AssignmentIcon fontSize='large' />}
            title='Total de Templates'
            value={serviceOrderMessages.length + salesMessages.length}
            subtitle={`${serviceOrderMessages.length} OS • ${salesMessages.length} Vendas`}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatsCard
            icon={<CodeIcon fontSize='large' />}
            title='Com Variáveis'
            value={getMessagesWithVariables()}
            subtitle='Templates dinâmicos'
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatsCard
            icon={<StarIcon fontSize='large' />}
            title='Favoritos'
            value={favorites.size}
            subtitle='Templates marcados'
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatsCard
            icon={<TimelineIcon fontSize='large' />}
            title='Ativo Agora'
            value={getCurrentMessages().length}
            subtitle='Filtro atual'
          />
        </Grid>
      </Grid>

      <Card elevation={3} style={{ borderRadius: 16, overflow: 'hidden' }}>
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          indicatorColor='primary'
          textColor='primary'
          variant='fullWidth'
          style={{ backgroundColor: '#f8f9fa' }}
        >
          <Tab
            icon={<BuildIcon />}
            label='Ordens de Serviço'
            style={{
              fontSize: 16,
              fontWeight: 'bold',
              textTransform: 'none',
              padding: '16px 24px',
            }}
          />
          <Tab
            icon={<ShoppingCartIcon />}
            label='Vendas'
            style={{
              fontSize: 16,
              fontWeight: 'bold',
              textTransform: 'none',
              padding: '16px 24px',
            }}
          />
        </Tabs>

        {/* Conteúdo das abas */}
        <Box style={{ padding: 24 }}>
          {/* Tab Panel - Ordens de Serviço */}
          {currentTab === 0 && (
            <Fade in timeout={500}>
              <Grid container spacing={4}>
                <Grid item xs={12} lg={5}>
                  {renderMessageForm()}
                </Grid>

                <Grid item xs={12} lg={7}>
                  <Card elevation={3} style={{ borderRadius: 16, height: 'fit-content' }}>
                    <CardHeader
                      avatar={
                        <Avatar style={{ backgroundColor: '#1976d2' }}>
                          <AssignmentIcon />
                        </Avatar>
                      }
                      title='Templates de Ordens de Serviço'
                      subheader={`${filteredServiceMessages.length} de ${serviceOrderMessages.length} templates`}
                      action={
                        <Box display='flex' alignItems='center' gap={1}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={showOnlyFavorites}
                                onChange={(e) => setShowOnlyFavorites(e.target.checked)}
                                color='primary'
                              />
                            }
                            label={
                              <Box display='flex' alignItems='center'>
                                <StarIcon style={{ marginRight: 4, fontSize: 18 }} />
                                Favoritos
                              </Box>
                            }
                          />
                        </Box>
                      }
                    />

                    <CardContent>
                      <TextField
                        fullWidth
                        size='small'
                        placeholder='Buscar templates...'
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position='start'>
                              <SearchIcon />
                            </InputAdornment>
                          ),
                        }}
                        style={{ marginBottom: 24 }}
                      />

                      {loadingMessages ? (
                        <Box>
                          <LinearProgress style={{ marginBottom: 16 }} />
                          <Typography variant='body2' color='textSecondary' align='center'>
                            Carregando templates...
                          </Typography>
                        </Box>
                      ) : filteredServiceMessages.length === 0 ? (
                        <Paper
                          elevation={0}
                          style={{
                            padding: 48,
                            textAlign: 'center',
                            backgroundColor: '#f8f9fa',
                            borderRadius: 12,
                          }}
                        >
                          <AssignmentIcon
                            style={{ fontSize: 64, color: '#bdbdbd', marginBottom: 16 }}
                          />
                          <Typography variant='h6' color='textSecondary' gutterBottom>
                            {searchTerm || showOnlyFavorites
                              ? 'Nenhum template encontrado'
                              : 'Nenhum template cadastrado'}
                          </Typography>
                          <Typography variant='body2' color='textSecondary'>
                            {searchTerm || showOnlyFavorites
                              ? 'Tente ajustar os filtros de busca'
                              : 'Crie seu primeiro template de ordem de serviço usando o formulário ao lado'}
                          </Typography>
                        </Paper>
                      ) : (
                        <Box style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                          {filteredServiceMessages.map((template) => (
                            <ModernTemplateCard
                              key={template.idCadastro}
                              template={template}
                              onEdit={handleEdit}
                              onDelete={handleDeleteConfirm}
                              onPreview={handlePreview}
                              onToggleFavorite={handleToggleFavorite}
                              isFavorite={favorites.has(template.idCadastro)}
                            />
                          ))}
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Fade>
          )}

          {/* Tab Panel - Vendas */}
          {currentTab === 1 && (
            <Fade in timeout={500}>
              <Grid container spacing={4}>
                <Grid item xs={12} lg={5}>
                  {renderMessageForm()}
                </Grid>

                <Grid item xs={12} lg={7}>
                  <Card elevation={3} style={{ borderRadius: 16, height: 'fit-content' }}>
                    <CardHeader
                      avatar={
                        <Avatar style={{ backgroundColor: '#f57c00' }}>
                          <ShoppingCartIcon />
                        </Avatar>
                      }
                      title='Templates de Vendas'
                      subheader={`${filteredSalesMessages.length} de ${salesMessages.length} templates`}
                      action={
                        <Box display='flex' alignItems='center' gap={1}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={showOnlyFavorites}
                                onChange={(e) => setShowOnlyFavorites(e.target.checked)}
                                color='primary'
                              />
                            }
                            label={
                              <Box display='flex' alignItems='center'>
                                <StarIcon style={{ marginRight: 4, fontSize: 18 }} />
                                Favoritos
                              </Box>
                            }
                          />
                        </Box>
                      }
                    />

                    <CardContent>
                      <TextField
                        fullWidth
                        size='small'
                        placeholder='Buscar templates...'
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position='start'>
                              <SearchIcon />
                            </InputAdornment>
                          ),
                        }}
                        style={{ marginBottom: 24 }}
                      />

                      {loadingMessages ? (
                        <Box>
                          <LinearProgress style={{ marginBottom: 16 }} />
                          <Typography variant='body2' color='textSecondary' align='center'>
                            Carregando templates...
                          </Typography>
                        </Box>
                      ) : filteredSalesMessages.length === 0 ? (
                        <Paper
                          elevation={0}
                          style={{
                            padding: 48,
                            textAlign: 'center',
                            backgroundColor: '#f8f9fa',
                            borderRadius: 12,
                          }}
                        >
                          <ShoppingCartIcon
                            style={{ fontSize: 64, color: '#bdbdbd', marginBottom: 16 }}
                          />
                          <Typography variant='h6' color='textSecondary' gutterBottom>
                            {searchTerm || showOnlyFavorites
                              ? 'Nenhum template encontrado'
                              : 'Nenhum template cadastrado'}
                          </Typography>
                          <Typography variant='body2' color='textSecondary'>
                            {searchTerm || showOnlyFavorites
                              ? 'Tente ajustar os filtros de busca'
                              : 'Crie seu primeiro template de vendas usando o formulário ao lado'}
                          </Typography>
                        </Paper>
                      ) : (
                        <Box style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                          {filteredSalesMessages.map((template) => (
                            <ModernTemplateCard
                              key={template.idCadastro}
                              template={template}
                              onEdit={handleEdit}
                              onDelete={handleDeleteConfirm}
                              onPreview={handlePreview}
                              onToggleFavorite={handleToggleFavorite}
                              isFavorite={favorites.has(template.idCadastro)}
                            />
                          ))}
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Fade>
          )}
        </Box>
      </Card>

      {/* Dialogs */}
      <PreviewDialog
        isOpen={openPreview}
        handleClose={() => setOpenPreview(false)}
        previewContent={previewContent}
      />

      <DeleteConfirmationDialog
        setSnackbar={setSnackbar}
        refetchMessages={refetchMessages}
        templateToDelete={templateToDelete}
        isOpen={openDeleteConfirm}
        handleClose={() => setOpenDeleteConfirm(false)}
      />

      {/* <SnackbarFeedback snackbar={snackbar} setSnackbar={setSnackbar} /> */}
    </Box>
  );
}
